import { useRef, useState, useEffect } from 'react';
import useWebSocket from '../../api/websocket';
import api from '../../api/client';

export default function RadioPlayer({ compact = false }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [live, setLive] = useState(null);
  const retryTimer = useRef(null);
  const retries = useRef(0);
  const lastTime = useRef(0);
  const stalledSince = useRef(0);
  const loadingSince = useRef(0);
  const lastNowKey = useRef(null);

  const streamUrl = import.meta.env.VITE_STREAM_URL || '/stream';

  // Heartbeat de oyente: le dice al backend (con nuestra IP real via X-Real-IP)
  // que estamos escuchando, para el mapa de ciudades del dashboard.
  const geoPingTimer = useRef(null);
  const sendGeoPing = () => {
    api.post('/api/stats/listener-ping', {
      src: 'monitor',
      referrer: window.location.href,
      user_agent: navigator.userAgent,
    }).catch(() => {});
  };
  useEffect(() => {
    if (playing) {
      sendGeoPing();
      if (geoPingTimer.current) clearInterval(geoPingTimer.current);
      geoPingTimer.current = setInterval(sendGeoPing, 30000);
    } else if (geoPingTimer.current) {
      clearInterval(geoPingTimer.current);
      geoPingTimer.current = null;
    }
    return () => {
      if (geoPingTimer.current) {
        clearInterval(geoPingTimer.current);
        geoPingTimer.current = null;
      }
    };
  }, [playing]);

  const { subscribe } = useWebSocket((data) => {
    if (data.type === 'now_playing') {
      setLive(data.payload);
    }
  });

  useEffect(() => {
    const unsub = subscribe('now_playing', (data) => setLive(data.payload));
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, []);

  const reconnect = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (retryTimer.current) clearTimeout(retryTimer.current);
    const backoff = Math.min(1500 + retries.current * 1000, 8000);
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      const a = audioRef.current;
      if (!a) return;
      try {
        a.src = `${streamUrl}?ts=${Date.now()}`;
        a.load();
        a.play().then(() => setPlaying(true)).catch(() => {});
      } catch (e) {
        // ignore
      }
    }, backoff);
    retries.current += 1;
  };

  // Detecta stream congelado/sin datos y reconecta automáticamente.
  // Clave: tras un reinicio del engine, el source se re-conecta y el reproductor
  // debe re-engancharse solo (evita que el oyente quede pegado en el standby).
  useEffect(() => {
    const iv = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.error) { reconnect(); return; }
      if (audio.paused && playing) { setPlaying(false); return; }
      if (!audio.paused && audio.readyState === 0) {
        loadingSince.current += 1;
        if (loadingSince.current >= 2) {
          loadingSince.current = 0;
          reconnect();
        }
        return;
      }
      if (!audio.paused && audio.readyState >= 2) {
        const t = audio.currentTime;
        if (t === lastTime.current) {
          stalledSince.current += 1;
          if (stalledSince.current >= 2) {
            stalledSince.current = 0;
            reconnect();
          }
        } else {
          lastTime.current = t;
          stalledSince.current = 0;
        }
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [playing]);

  // Si la pestaña vuelve a estar visible y el stream quedó pegado, reconecta
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        const audio = audioRef.current;
        if (audio && !audio.paused && audio.error) reconnect();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Detecta el reinicio del engine: cuando el mismo track cambia su started_at,
  // el engine reinició y reanudó → re-engancha el audio para no quedarse en standby.
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get('/api/playback/now-playing');
        const mid = data?.media_file_id || null;
        const startedAt = data?.started_at || null;
        if (!mid || !startedAt) {
          lastNowKey.current = null;
          return;
        }
        const key = `${mid}@${startedAt}`;
        const prev = lastNowKey.current;
        lastNowKey.current = key;
        if (prev) {
          const at = key.indexOf('@');
          const prevAt = prev.indexOf('@');
          const prevMid = prev.slice(0, prevAt);
          if (prevMid === mid && prev.slice(prevAt + 1) !== String(startedAt)) {
            const audio = audioRef.current;
            if (audio && !audio.paused) reconnect();
          }
        }
      } catch (e) {
        // ignore
      }
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      retries.current = 0;
      audio.play().catch(() => setPlaying(false));
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-dark-800 border border-dark-700 px-3 py-1.5">
        <span className="flex items-center gap-1.5">
          <span className={`relative flex h-2 w-2 ${playing ? '' : ''}`}>
            {playing && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-400 opacity-75"></span>
            )}
            <span className={`relative inline-flex h-2 w-2 rounded-full ${playing ? 'bg-success-400' : 'bg-dark-500'}`}></span>
          </span>
          <span className="text-xs font-semibold text-dark-200">EN VIVO</span>
        </span>
        <button
          type="button"
          onClick={toggle}
          className={`flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors ${playing ? 'bg-dark-600 hover:bg-dark-500' : 'bg-primary-600 hover:bg-primary-700'}`}
          title={playing ? 'Pausar radio' : 'Escuchar radio'}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          className="w-16 accent-primary-500"
          title="Volumen"
        />
        {live && (
          <span className="hidden md:block max-w-[180px] truncate text-xs text-dark-300" title={`${live.artist || ''} - ${live.title || ''}`}>
            {live.artist ? `${live.artist} - ` : ''}{live.title || ''}
          </span>
        )}
      <audio ref={audioRef} src={streamUrl} preload="none" onPlay={() => { retries.current = 0; stalledSince.current = 0; setPlaying(true); }} onPause={() => setPlaying(false)} onEnded={reconnect} onError={reconnect} />
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-gradient-to-r from-primary-900/40 to-secondary-900/40 border border-primary-700/50 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-4">
          <div className="relative flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
            📻
            {playing && (
              <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-success-400"></span>
              </span>
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold text-dark-100">Radio en vivo</h2>
            <p className="text-sm text-dark-400">
              {playing ? 'Escuchando la transmisión...' : 'Escuchá lo que sale del sistema'}
            </p>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-3 sm:justify-end">
          <button
            type="button"
            onClick={toggle}
            className={`btn ${playing ? 'btn-secondary' : 'btn-primary'} btn-lg`}
          >
            {playing ? '❚❚ Pausar' : '▶ Escuchar radio'}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-24 accent-primary-500"
            title="Volumen"
          />
        </div>
      </div>

  <audio ref={audioRef} src={streamUrl} preload="none" onPlay={() => { retries.current = 0; stalledSince.current = 0; setPlaying(true); }} onPause={() => setPlaying(false)} onEnded={reconnect} onError={reconnect} />
    </div>
  );
}
